/**
 * Shared Studio inspector controls (rows, sliders, segmented buttons, toggles,
 * font picker, motion-phase editor) used by the Hooks, Emphasis, Edit-style and
 * word inspectors so they look and behave the same.
 */
import { FONT_REGISTRY } from '../fontList';
import type { HookEasing, HookIdle, HookMotionKind, HookPhase } from '@captionflow/caption-engine';

export const WEIGHTS: { v: string; label: string }[] = [
  { v: '300', label: 'Thin' },
  { v: '500', label: 'Reg' },
  { v: '700', label: 'Bold' },
  { v: '900', label: 'Black' },
];
export const IN_KINDS: { v: HookMotionKind; label: string }[] = [
  { v: 'fade', label: 'Fade' },
  { v: 'slideUp', label: 'Rise' },
  { v: 'slideDown', label: 'Fall' },
  { v: 'slideRight', label: 'From left' },
  { v: 'slideLeft', label: 'From right' },
  { v: 'mask', label: 'Mask' },
  { v: 'blur', label: 'Blur' },
  { v: 'zoomIn', label: 'Zoom in' },
  { v: 'zoomOut', label: 'Zoom out' },
  { v: 'punch', label: 'Punch' },
  { v: 'shake', label: 'Shake' },
  { v: 'drop', label: 'Drop' },
  { v: 'spin', label: 'Spin' },
  { v: 'trackIn', label: 'Tracking' },
  { v: 'none', label: 'Cut' },
];
export const EASINGS: { v: HookEasing; label: string }[] = [
  { v: 'expo', label: 'Smooth (expo)' },
  { v: 'easeOut', label: 'Ease out' },
  { v: 'easeInOut', label: 'Ease in-out' },
  { v: 'back', label: 'Overshoot' },
  { v: 'spring', label: 'Spring' },
  { v: 'bounce', label: 'Bounce' },
  { v: 'linear', label: 'Linear' },
];
export const IDLES: { v: HookIdle; label: string }[] = [
  { v: 'none', label: 'None' },
  { v: 'pushIn', label: 'Push in' },
  { v: 'float', label: 'Float' },
  { v: 'pulse', label: 'Pulse' },
  { v: 'drift', label: 'Drift' },
  { v: 'sway', label: 'Sway' },
  { v: 'shake', label: 'Jitter' },
];

export function hex(value: string | undefined, fallback: string): string {
  if (value && /^#[0-9a-fA-F]{6}$/.test(value)) return value;
  return fallback;
}
export function secs(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}
export const pct = (v: number) => `${Math.round(v * 100)}%`;

/** Drop keys whose value is undefined (so cleared overrides really clear). */
export function compact<T extends object>(o: T): T {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as T;
}

/** Duration / distance / blur / easing / fade for one motion phase. */
export function PhaseControls<K extends string>({ phase, onChange, kind }: {
  phase: HookPhase<K>;
  onChange: (p: Partial<HookPhase<K>>) => void;
  kind: string;
}) {
  const hasDistance = !['fade', 'none', 'mirror', 'blur'].includes(kind);
  const distLabel = kind === 'spin' ? 'Turn' : kind.startsWith('zoom') || kind === 'punch' || kind === 'shake' ? 'Amount'
    : kind === 'trackIn' ? 'Spread' : kind === 'mask' ? 'Travel' : 'Distance';
  return (
    <>
      <Slider label="Duration" min={60} max={2500} step={10} value={phase.durationMs}
        onChange={(v) => onChange({ durationMs: v } as Partial<HookPhase<K>>)} display={secs(phase.durationMs)} />
      {hasDistance && (
        <Slider label={distLabel} min={0} max={kind.startsWith('slide') || kind === 'drop' ? 0.8 : 1} step={0.01} value={phase.distance}
          onChange={(v) => onChange({ distance: v } as Partial<HookPhase<K>>)} display={pct(phase.distance)} />
      )}
      {kind !== 'mirror' && (
        <Slider label="Motion blur" min={0} max={1} step={0.02} value={phase.blur}
          onChange={(v) => onChange({ blur: v } as Partial<HookPhase<K>>)} display={pct(phase.blur)} />
      )}
      <Row label="Easing">
        <select className="ses-select" value={phase.easing} onChange={(e) => onChange({ easing: e.target.value as HookEasing } as Partial<HookPhase<K>>)}>
          {EASINGS.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
        </select>
      </Row>
      {kind !== 'mirror' && kind !== 'fade' && (
        <Row label="Fade with it">
          <Toggle on={phase.fade ?? true} onClick={() => onChange({ fade: !(phase.fade ?? true) } as Partial<HookPhase<K>>)} />
        </Row>
      )}
    </>
  );
}

export function nearestWeight(w: number): number {
  return [300, 500, 700, 900].reduce((best, x) => (Math.abs(x - w) < Math.abs(best - w) ? x : best), 900);
}

export function FontSelect({ value, onChange, inherit }: { value: string; onChange: (v: string) => void; inherit?: boolean }) {
  return (
    <select className="ses-select" value={value} onChange={(e) => onChange(e.target.value)}>
      {inherit && <option value="">Same as hook</option>}
      {FONT_REGISTRY.map((f) => <option key={f.family} value={f.family}>{f.displayName}</option>)}
    </select>
  );
}

export function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="shk-row"><span>{label}</span>{children}</div>;
}
export function Slider({ label, min, max, step, value, onChange, display }: {
  label: string; min: number; max: number; step: number; value: number; onChange: (v: number) => void; display: string;
}) {
  return (
    <div className="shk-row">
      <span>{label}</span>
      <div className="shk-size">
        <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} />
        <span>{display}</span>
      </div>
    </div>
  );
}
export function Seg<T extends string>({ options, value, onPick, wrap }: {
  options: { v: T; label: string }[]; value: T; onPick: (v: T) => void; wrap?: boolean;
}) {
  return (
    <div className={wrap ? 'ses-seg wrap' : 'ses-seg'}>
      {options.map((o) => (
        <button key={o.v} className={value === o.v ? 'on' : ''} onClick={() => onPick(o.v)}>{o.label}</button>
      ))}
    </div>
  );
}
export function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button className={on ? 'shk-toggle on' : 'shk-toggle'} onClick={onClick} aria-pressed={on}>
      <span className="shk-toggle-knob" />
    </button>
  );
}
